import { useCallback, useEffect, useRef, useState } from "react";

import { apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";

interface RecurringEntry {
  day: number;
  start: string;
  end: string;
}

interface RecurringGroup {
  days: number[];
  start: string;
  end: string;
}

interface AdhocEntry {
  starts_at: string;
  expires_at: string;
}

interface FreePlayTimesData {
  timezone: string;
  recurring: RecurringEntry[];
  adhoc: AdhocEntry[];
  is_free_play: boolean;
  free_play_ends_at?: string;
}

const TIMEZONES = Intl.supportedValuesOf("timeZone");

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function groupRecurring(entries: RecurringEntry[]): RecurringGroup[] {
  const groups: RecurringGroup[] = [];
  for (const entry of entries) {
    const existing = groups.find((g) => g.start === entry.start && g.end === entry.end);
    if (existing) {
      if (!existing.days.includes(entry.day)) existing.days.push(entry.day);
    } else {
      groups.push({ days: [entry.day], start: entry.start, end: entry.end });
    }
  }
  return groups;
}

function flattenGroups(groups: RecurringGroup[]): RecurringEntry[] {
  return groups.flatMap((g) => g.days.map((day) => ({ day, start: g.start, end: g.end })));
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const day = DAY_NAMES[d.getDay()];
  return `${day} ${d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23" })}`;
}

function formatRemainingTime(endsAt: string): string {
  const ms = new Date(endsAt).getTime() - Date.now();
  if (ms <= 0) return "ending now";
  const minutes = Math.ceil(ms / 60000);
  if (minutes < 60) return `${minutes}m remaining`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem > 0 ? `${hours}h ${rem}m remaining` : `${hours}h remaining`;
}

function isAdhocActive(entry: AdhocEntry): boolean {
  const now = Date.now();
  return new Date(entry.starts_at).getTime() <= now && new Date(entry.expires_at).getTime() > now;
}

function isAdhocUpcoming(entry: AdhocEntry): boolean {
  return new Date(entry.starts_at).getTime() > Date.now();
}

export function FreePlayTimes() {
  const { selectedVenue } = useAuth();
  const [data, setData] = useState<FreePlayTimesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [timezone, setTimezone] = useState("Europe/London");
  const [groups, setGroups] = useState<RecurringGroup[]>([]);
  const [adhoc, setAdhoc] = useState<AdhocEntry[]>([]);

  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [, setTick] = useState(0);

  const fetchData = useCallback(async () => {
    if (!selectedVenue) return;
    try {
      const res = await apiClient["/api/v1/venue/free-play-times"]["get"]({
        parameters: { venue_id: selectedVenue.id },
        body: null,
      });
      if (res.code === 200) {
        const body = res.body as FreePlayTimesData;
        setData(body);
        setTimezone(body.timezone);
        setGroups(groupRecurring(body.recurring));
        setAdhoc(body.adhoc);
      } else {
        setError("Failed to load free play times");
      }
    } catch {
      setError("Failed to load free play times");
    } finally {
      setLoading(false);
    }
  }, [selectedVenue]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data?.is_free_play && data.free_play_ends_at) {
      countdownRef.current = setInterval(() => setTick((t) => t + 1), 30000);
      return () => {
        if (countdownRef.current) clearInterval(countdownRef.current);
      };
    }
  }, [data?.is_free_play, data?.free_play_ends_at]);

  const applyResponse = (body: FreePlayTimesData) => {
    setData(body);
    setTimezone(body.timezone);
    setGroups(groupRecurring(body.recurring));
    setAdhoc(body.adhoc);
  };

  const saveAll = async (
    overrideGroups?: RecurringGroup[],
    overrideAdhoc?: AdhocEntry[],
    overrideTimezone?: string,
  ) => {
    if (!selectedVenue) return;
    setSaving(true);
    setError("");
    setSaveSuccess(false);
    try {
      const res = await apiClient["/api/v1/venue/free-play-times"]["put"]({
        parameters: { venue_id: selectedVenue.id },
        body: {
          timezone: overrideTimezone ?? timezone,
          recurring: flattenGroups(overrideGroups ?? groups),
          adhoc: overrideAdhoc ?? adhoc,
        },
      });
      if (res.code === 200) {
        applyResponse(res.body as FreePlayTimesData);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        const errBody = res.body as { message?: string };
        const detail = errBody?.message ?? `HTTP ${res.code}`;
        setError(`Failed to save (${res.code}): ${detail}`);
      }
    } catch {
      setError("Failed to save (network error — check connection and try again)");
    } finally {
      setSaving(false);
    }
  };

  const handleAddAdhocHour = async () => {
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 60 * 60 * 1000);
    const newEntry: AdhocEntry = {
      starts_at: now.toISOString(),
      expires_at: expiresAt.toISOString(),
    };
    const newAdhoc = [...adhoc, newEntry];
    setAdhoc(newAdhoc);
    await saveAll(undefined, newAdhoc);
  };

  const removeAdhoc = async (index: number) => {
    const newAdhoc = adhoc.filter((_, i) => i !== index);
    setAdhoc(newAdhoc);
    await saveAll(undefined, newAdhoc);
  };

  const addGroup = () => {
    setGroups([...groups, { days: [1], start: "09:00", end: "10:00" }]);
  };

  const removeGroup = (index: number) => {
    setGroups(groups.filter((_, i) => i !== index));
  };

  const toggleDay = (groupIndex: number, day: number) => {
    setGroups(
      groups.map((g, i) => {
        if (i !== groupIndex) return g;
        const days = g.days.includes(day) ? g.days.filter((d) => d !== day) : [...g.days, day];
        return { ...g, days };
      }),
    );
  };

  const updateGroupTime = (index: number, field: "start" | "end", value: string) => {
    setGroups(groups.map((g, i) => (i === index ? { ...g, [field]: value } : g)));
  };

  if (loading) {
    return (
      <main className="p-6">
        <h1 className="text-2xl font-bold text-text-primary mb-6">Free Play Times</h1>
        <p className="text-text-muted text-sm">Loading...</p>
      </main>
    );
  }

  const activeOrUpcomingAdhoc = adhoc.filter((e) => isAdhocActive(e) || isAdhocUpcoming(e));

  return (
    <main className="p-6">
      <h1 className="text-2xl font-bold text-text-primary mb-6">Free Play Times</h1>

      {error && (
        <div className="mb-4 px-4 py-2 rounded-md bg-red-500/20 text-red-400 text-sm">{error}</div>
      )}

      {/* Current status */}
      <div className="bg-surface-raised border border-border rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-text-primary">Current Status</h2>
            {data?.is_free_play ? (
              <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/20 text-green-400">
                Free Play Active
              </span>
            ) : (
              <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
                Not Active
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {data?.is_free_play && data.free_play_ends_at && (
              <span className="text-sm text-text-secondary">
                {formatRemainingTime(data.free_play_ends_at)}
              </span>
            )}
            <button
              onClick={handleAddAdhocHour}
              disabled={saving}
              className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving..." : "+ 1 Hour Free Play"}
            </button>
          </div>
        </div>
      </div>

      {/* One-off free play times */}
      <div className="bg-surface-raised border border-border rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">One-Off Free Play</h2>

        {activeOrUpcomingAdhoc.length === 0 ? (
          <p className="text-text-muted text-sm">
            No one-off free play times. Use the button above to add one.
          </p>
        ) : (
          <div className="grid gap-2">
            {activeOrUpcomingAdhoc.map((entry) => {
              const originalIndex = adhoc.indexOf(entry);
              const active = isAdhocActive(entry);
              return (
                <div
                  key={originalIndex}
                  className="flex items-center justify-between p-3 rounded-md border border-border bg-surface"
                >
                  <div className="flex items-center gap-3">
                    {active ? (
                      <span className="inline-block w-2 h-2 rounded-full bg-green-400" />
                    ) : (
                      <span className="inline-block w-2 h-2 rounded-full bg-gray-500" />
                    )}
                    <span className="text-sm text-text-primary">
                      {formatDateTime(entry.starts_at)}
                    </span>
                    <span className="text-text-muted text-sm">to</span>
                    <span className="text-sm text-text-primary">
                      {formatDateTime(entry.expires_at)}
                    </span>
                    {active && (
                      <span className="text-xs text-green-400">
                        {formatRemainingTime(entry.expires_at)}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => removeAdhoc(originalIndex)}
                    disabled={saving}
                    className="px-2 py-1 text-sm rounded-md text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Timezone */}
      <div className="bg-surface-raised border border-border rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-text-primary mb-3">Timezone</h2>
        <select
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          className="w-full max-w-xs px-3 py-2 text-sm rounded-md bg-surface border border-border text-text-primary focus:outline-none focus:border-accent transition-colors"
        >
          {TIMEZONES.map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
      </div>

      {/* Recurring schedule */}
      <div className="bg-surface-raised border border-border rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">Recurring Schedule</h2>
          <button
            onClick={addGroup}
            className="px-3 py-1.5 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary hover:border-accent transition-colors"
          >
            Add Period
          </button>
        </div>

        {groups.length === 0 && (
          <p className="text-text-muted text-sm">
            No recurring free play times configured. Click &quot;Add Period&quot; to create one.
          </p>
        )}

        {groups.length > 0 && (
          <div className="grid gap-3">
            {groups.map((group, index) => (
              <div
                key={index}
                className="p-3 rounded-md border border-border bg-surface"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex gap-1">
                    {DAY_LABELS.map((label, dayIndex) => (
                      <button
                        key={dayIndex}
                        onClick={() => toggleDay(index, dayIndex)}
                        title={DAY_NAMES[dayIndex]}
                        className={`w-8 h-8 text-xs font-medium rounded-md transition-colors ${
                          group.days.includes(dayIndex)
                            ? "bg-accent text-white"
                            : "bg-surface border border-border text-text-muted hover:text-text-primary hover:border-accent"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  <input
                    type="time"
                    value={group.start}
                    onChange={(e) => updateGroupTime(index, "start", e.target.value)}
                    className="input-time-dark px-2 py-1.5 text-sm rounded-md bg-surface border border-border text-text-primary focus:outline-none focus:border-accent transition-colors"
                  />

                  <span className="text-text-muted text-sm">to</span>

                  <input
                    type="time"
                    value={group.end}
                    onChange={(e) => updateGroupTime(index, "end", e.target.value)}
                    className="input-time-dark px-2 py-1.5 text-sm rounded-md bg-surface border border-border text-text-primary focus:outline-none focus:border-accent transition-colors"
                  />

                  <button
                    onClick={() => removeGroup(index)}
                    className="ml-auto px-2 py-1 text-sm rounded-md text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={() => saveAll()}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save Schedule"}
          </button>
          {saveSuccess && <span className="text-sm text-green-400">Saved</span>}
        </div>
      </div>
    </main>
  );
}
